import React, { useState, useEffect, useRef } from 'react';
import { Menu, X } from 'lucide-react';
import CardSwap, { Card } from './components/CardSwap';
import ElectricBorder from './components/ElectricBorder';

const HERO_PHRASES = [
  'Web Development',
  'Performance Engineering',
  'Test Automation',
  'Full-Stack Builds'
];

const SCRAMBLE_CHARS = '!<>-_\\/[]{}—=+*^?#abcdef0123456789';

function GlitchText({
  phrases,
  typeDelay = 70,
  scrambleSteps = 6,
  scrambleDelay = 50,
  pauseTime = 2200,
  className = ''
}) {
  const [display, setDisplay] = useState('');
  const [index, setIndex] = useState(0);

  useEffect(() => {
    let timeoutId;
    let cancelled = false;
    const target = phrases[index] ?? '';

    const rand = (n) =>
      Array.from({ length: n }, () =>
        SCRAMBLE_CHARS[Math.floor(Math.random() * SCRAMBLE_CHARS.length)]
      ).join('');

    let i = 0;
    const typeIn = () => {
      if (cancelled) return;
      if (i > target.length) {
        timeoutId = setTimeout(() => scramble(0), pauseTime);
        return;
      }
      const ahead = Math.min(3, target.length - i);
      setDisplay(target.slice(0, i) + rand(ahead));
      i += 1;
      timeoutId = setTimeout(typeIn, typeDelay);
    };

    const scramble = (step) => {
      if (cancelled) return;
      if (step >= scrambleSteps) {
        setIndex((prev) => (prev + 1) % phrases.length);
        return;
      }
      setDisplay(rand(Math.max(target.length, 1)));
      timeoutId = setTimeout(() => scramble(step + 1), scrambleDelay);
    };

    typeIn();

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [index, phrases, typeDelay, scrambleSteps, scrambleDelay, pauseTime]);

  return (
    <span className={className}>
      <span>{display || ' '}</span>
      <span
        aria-hidden="true"
        className="inline-block w-[3px] ml-2 align-middle bg-lime-400"
        style={{ height: '0.85em', animation: 'glitch-blink 1s steps(2) infinite' }}
      />
    </span>
  );
}

function Reveal({ children, className = '', delay = 0 }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (!ref.current) return;
    if (typeof IntersectionObserver === 'undefined') {
      setShown(true);
      return;
    }
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShown(true);
          obs.disconnect();
        }
      },
      { threshold: 0.12, rootMargin: '0px 0px -6% 0px' }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: shown ? 1 : 0,
        transform: shown ? 'translateY(0)' : 'translateY(40px)',
        transition: 'opacity 0.9s ease-out, transform 0.9s ease-out',
        transitionDelay: `${delay}ms`,
        willChange: 'opacity, transform'
      }}
    >
      {children}
    </div>
  );
}

export default function Portfolio() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 500);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

const heroRef = useRef(null);
const aboutRef = useRef(null);
const projectsRef = useRef(null);
const contactRef = useRef(null);

  useEffect(() => {
    const sections = [
      { name: 'home', el: heroRef.current },
      { name: 'about', el: aboutRef.current },
      { name: 'projects', el: projectsRef.current },
      { name: 'contact', el: contactRef.current }
    ].filter((s) => s.el);

    if (sections.length === 0 || typeof IntersectionObserver === 'undefined') return;

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const match = sections.find((s) => s.el === entry.target);
            if (match) setActiveSection(match.name);
          }
        });
      },
      { rootMargin: '-40% 0px -50% 0px', threshold: 0 }
    );

    sections.forEach((s) => obs.observe(s.el));
    return () => obs.disconnect();
  }, []);

  const projects = [
    {
      title: "Performance Framework",
      description: "A standalone framework for monitoring system behaviour and metrics, built from the ground up with Grafana's k6 load testing tool. Results stream into Grafana dashboards, and runs can be triggered on demand from Azure DevOps so any tester, manual or automation, can launch a load test without touching code.",
      tech: ["Typescript", "Node.js", "K6"],
      image: "/images/grafanaLabs.png"
    },
    {
      title: "Atlassian Framework",
      description: "Python framework to automatically generate Confluence pages based on user stories and tasks. Pulls directly from Jira so documentation stays in sync with the board, cutting hours of manual write-up out of every sprint.",
      tech: ["Python", "Javascript", "Tailwind"],
      image: "/images/atlassian.png"
    },
    {
      title: "XR Store Planner",
      description: "Meta Quest 2/3 app for designing retail store layouts in 3D, with a WPF 2D companion and a Gradient Boosted Tree model suggesting optimal product placement. Adopted by Salling Group for continued use.",
      tech: ["C#", "Unity", "WPF", "ML", "Oracle"],
      image: "/images/dataverseWizards.png"
    }
  ];

  const skills = [
    { category: "Frontend", items: ["React", "Next.js", "TypeScript", "JavaScript", "HTML/CSS", "Tailwind CSS"] },
    { category: "Backend & Data", items: ["Node.js", "Python", "Java", "C#", ".NET", "PostgreSQL", "MySQL"] },
    { category: "Testing & Performance", items: ["Playwright", "Grafana k6", "REST APIs", "Allure"] },
    { category: "DevOps & Tools", items: ["Docker", "Azure DevOps", "GitHub Actions", "Git", "Linux", "Google Cloud"] }
  ];

const scrollToSection = (section) => {
  setActiveSection(section);
  setIsMenuOpen(false);

  if (section === 'home') {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    return;
  }

  const refs = {
    about: aboutRef,
    projects: projectsRef,
    contact: contactRef
  };

  refs[section]?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

  return (
          <div
            className="min-h-screen overflow-x-hidden text-white"
            style={{ backgroundColor: '#030113' }}
          >

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@700;900&display=swap');

        .cyber-text {
          font-family: 'Orbitron', sans-serif;
          font-style: italic;
        }

        .katana-nav {
          max-height: 400px;
        }

        @media (max-width: 769px) {
          .katana-nav {
            max-height: 200px !important;
            transform: translateY(-10px) scale(1.2) !important;
          }
        }

        @keyframes glitch-blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }

        @media (prefers-reduced-motion: reduce) {
          .crt-overlay { display: none !important; }
        }
      `}</style>
      
     {/* Navigation - Katana Image Design */}
        <nav className="fixed w-full z-20 bg-black/80 backdrop-blur-lg">
          <div className="pt-4 pb-4 bg-black/80 backdrop-blur-lg">
            <div className="max-w-2xl mx-auto px-3">
              <div className="relative h-16 flex items-center justify-end md:justify-center">
                {/* Katana Image Background - desktop only */}
                <img
                  src="/images/katanaTopBar.png"
                  alt=""
                  className="katana-nav hidden md:block absolute w-full h-auto opacity-80"
                  style={{
                    transform: 'translateY(8px) scaleX(1.2) scaleY(1.5)',
                    filter: 'brightness(0.7) drop-shadow(0 0 15px rgba(190, 242, 100, 0.4))',
                    maxHeight: '2000px',
                    maxWidth: '100%'
                  }}
                />
            
            {/* Desktop Menu Buttons Overlaid on Blade */}
            <div className="hidden md:flex items-center justify-center gap-8 relative z-10"
                 style={{ transform: ' translateY(7px)' }}>
              {['Home', 'About', 'Projects', 'Contact'].map((item) => (
                <button
                  key={item}
                  onClick={() => scrollToSection(item.toLowerCase())}
                  className={`cyber-text px-4 py-2 
                    transition-all duration-300 hover:-translate-y-1 hover:drop-shadow-[0_0_12px_rgba(190,242,100,0.9)]
                    text-lime-400 font-black tracking-wider text-lg ${
                    activeSection === item.toLowerCase() ? 'text-lime-300 -translate-y-1 drop-shadow-[0_0_15px_rgba(190,242,100,1)]' : ''
                  }`}
                  style={{
                    textShadow: '0 0 15px rgba(190, 242, 100, 0.7), 0 0 25px rgba(190, 242, 100, 0.4), 2px 2px 4px rgba(0,0,0,0.8)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0px)';
                  }}
                >
                  <span style={{ display: 'inline-block' }}>
                    {item}
                  </span>
                </button>
              ))}
            </div>
          {/* Mobile Menu Button */}
          <button
            className="md:hidden relative z-10 group"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMenuOpen}
          >
            {/* Ambient pulse glow */}
            <span className="absolute -inset-1 bg-lime-500/30 blur-md animate-pulse rounded-md pointer-events-none" />

            {/* Outer notched frame */}
            <span
              className="absolute inset-0 bg-lime-500/40 group-hover:bg-lime-400/70 transition-colors duration-300"
              style={{
                clipPath:
                  'polygon(10px 0, 100% 0, 100% calc(100% - 10px), calc(100% - 10px) 100%, 0 100%, 0 10px)'
              }}
            />

            {/* Button face */}
            <span
              className="relative flex items-center gap-2 px-3.5 py-2 bg-black/85 backdrop-blur-sm text-lime-400 group-hover:text-lime-300 group-hover:shadow-[0_0_25px_rgba(190,242,100,0.6)] transition-all duration-300 m-[1px]"
              style={{
                clipPath:
                  'polygon(10px 0, 100% 0, 100% calc(100% - 10px), calc(100% - 10px) 100%, 0 100%, 0 10px)',
                textShadow: '0 0 10px rgba(190, 242, 100, 0.7)'
              }}
            >
              <span className="cyber-text text-[11px] tracking-[0.25em] font-black">
                {isMenuOpen ? 'CLOSE' : 'MENU'}
              </span>
              <span
                className={`flex transition-transform duration-300 ${
                  isMenuOpen ? 'rotate-180' : 'rotate-0'
                }`}
              >
                {isMenuOpen ? <X size={18} strokeWidth={2.5} /> : <Menu size={18} strokeWidth={2.5} />}
              </span>
            </span>
          </button>
          </div>
        </div>
                    </div>

        {/* Mobile Menu Dropdown */}
        {isMenuOpen && (
          <div className="md:hidden mt-2 mx-4 bg-gray-900/95 backdrop-blur-sm rounded-lg border border-lime-500/40 shadow-[0_0_20px_rgba(190,242,100,0.15)]">
            <div className="px-4 py-2 divide-y divide-lime-500/10">
              {['Home', 'About', 'Projects', 'Contact'].map((item) => (
                <button
                  key={item}
                  onClick={() => scrollToSection(item.toLowerCase())}
                  className={`cyber-text block w-full text-center py-3 text-lime-400 hover:text-lime-300 transition-colors font-bold tracking-wider ${
                    activeSection === item.toLowerCase() ? 'text-lime-300' : ''
                  }`}
                >
                  {item}
                </button>
              ))}
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section ref={heroRef} className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-black via-gray-950 to-black border-b border-lime-500/10">
        <div className="max-w-6xl mx-auto text-center">
          <Reveal>
            <div className="mb-8">
                <div className="relative flex justify-center mb-10">
                {/* Neon glow */}
                <div
                  className="absolute inset-0 rounded-full blur-3xl opacity-70"
                  style={{
                    background:
                      'radial-gradient(circle, rgba(128,255,159,0.45) 0%, rgba(128,255,159,0.15) 30%, rgba(3,1,19,0) 70%)',
                  }}
                />

                {/* Logo */}
                <img
                  src="/images/logoPlaceholder.png"
                  alt="ZenTrue Logo"
                  className="relative z-10 w-full max-w-[1200px] h-auto object-contain"
                  style={{
                    filter: 'drop-shadow(0 0 25px rgba(128,255,159,0.45))',
                  }}
                />
              </div>
            </div>
            <h1
              className="cyber-text text-4xl sm:text-6xl font-black mb-6 text-lime-300 tracking-wider"
              style={{
                textShadow:
                  '0 0 20px rgba(190, 242, 100, 0.7), 0 0 40px rgba(190, 242, 100, 0.3)'
              }}
            >
              <GlitchText phrases={HERO_PHRASES} />
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Building beautiful, functional, and user-centered digital experiences
            </p>
          </Reveal>
        </div>
      </section>

      {/* Current Collaborators Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-950 relative overflow-hidden">
        {/* Desktop background */}
        <div
          className="absolute inset-0 hidden md:block"
          style={{
            backgroundImage: "url('/images/roboNinjas.png')",
            backgroundRepeat: "no-repeat",
            backgroundSize: "73%",
            backgroundPosition: "50% 32%",
          }}
        />

        {/* Mobile background */}
        <div
          className="absolute inset-0 block md:hidden"
          style={{
            backgroundImage: "url('/images/roboNinjas.png')",
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
            backgroundPosition: "50% 32%",
          }}
        />
      {/* Dark Overlay */}
      <div className="absolute inset-0 bg-black/60" />

     <Reveal className="max-w-6xl mx-auto relative z-10">
       <h2 className="text-3xl font-bold mb-8 text-center">Current Collaborators</h2>
       <div className="flex justify-center items-center">
         <ElectricBorder
           color="#80ff9f"
           speed={1}
           chaos={0.15}
           borderRadius={16}
         >
           <div className="bg-gray-800 rounded-lg p-8">
             <div className="flex items-center justify-center h-24">
               <img
                 src="/images/devhub_logo.jpg"
                 alt="DevHub"
                 className="h-20 object-contain"
               />
             </div>
           </div>
         </ElectricBorder>
       </div>
     </Reveal>
    </section>

      {/* About Section */}
      <section ref={aboutRef} className="py-20 px-4 sm:px-6 lg:px-8 bg-black border-t border-b border-lime-500/10">
        <Reveal className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold mb-12 text-center">About ZenTrue</h2>
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <p className="text-gray-300 mb-6 leading-relaxed">
                ZenTrue is a passionate development company focused on building web applications that solve real-world problems. 
                We love creating intuitive user interfaces and robust backend systems.
              </p>
              <p className="text-gray-300 leading-relaxed">
                When we're not coding, you can find us exploring new technologies, contributing to open source, 
                or sharing knowledge with the developer community.
              </p>
            </div>
            <div>
              <h3 className="text-2xl font-semibold mb-6">Skills</h3>
              <div className="space-y-4">
                {skills.map((skill) => (
                  <div key={skill.category}>
                    <h4 className="text-lime-400 font-semibold mb-2">{skill.category}</h4>
                    <div className="flex flex-wrap gap-2">
                      {skill.items.map((item) => (
                        <span
                          key={item}
                          className="bg-gray-900 px-3 py-1 rounded-full text-sm border border-lime-500/30"
                        >
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Reveal>
      </section>

       {/* Projects Section */}
          <section ref={projectsRef} className="py-20 px-2 sm:px-6 lg:px-8 pb-10 bg-gradient-to-b from-black via-gray-950 to-black overflow-hidden">
            <Reveal className="max-w-6xl mx-auto">
              <h2 className="text-4xl font-bold mb-12 text-center">Featured Projects</h2>
              <div
                style={{
                  height: windowWidth < 480 ? 660 : windowWidth < 768 ? 720 : 480,
                  minHeight: windowWidth < 480 ? '680px' : windowWidth < 768 ? '740px' : '520px',
                  position: 'relative'
                }}
                className="flex justify-center"
              >
                <CardSwap
                  width={windowWidth < 480 ? 280 : windowWidth < 768 ? 420 : 500}
                  height={windowWidth < 480 ? 520 : windowWidth < 768 ? 600 : 440}
                  cardDistance={windowWidth < 480 ? 4 : windowWidth < 768 ? 18 : 60}
                  verticalDistance={windowWidth < 480 ? 28 : windowWidth < 768 ? 40 : 60}
                  delay={4000}
                  pauseOnHover={true}
                >
              {projects.map((project, index) => (
                <Card key={index}>
                  <img src={project.image} alt={project.title} className="w-full h-48 object-cover rounded-t-lg" />
                  <div className="p-6">
                    <h3 className="text-xl font-semibold mb-2">{project.title}</h3>
                    <p className="text-gray-400 mb-4 text-sm">{project.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {project.tech.map((tech) => (
                        <span key={tech} className="text-xs bg-lime-500/20 px-2 py-1 rounded text-lime-300">
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                </Card>
              ))}
            </CardSwap>
          </div>
        </Reveal>
      </section>

      {/* Past Collaborators Section */}
   <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-950 border-t border-lime-500/10 relative overflow-hidden">
        {/* Desktop background */}
        <div
          className="absolute inset-0 hidden md:block"
          style={{
            backgroundImage: "url('/images/roboLeave.png')",
            backgroundRepeat: "no-repeat",
            backgroundSize: "73%",
            backgroundPosition: "50% 32%",
          }}
        />

        {/* Mobile background */}
        <div
          className="absolute inset-0 block md:hidden"
          style={{
            backgroundImage: "url('/images/roboLeave.png')",
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
            backgroundPosition: "50% 32%",
          }}
        />
      {/* Dark Overlay */}
      <div className="absolute inset-0 bg-black/60" />

     <Reveal className="max-w-6xl mx-auto relative z-10">
       <h2 className="text-3xl font-bold mb-8 text-center">Past Collaborators</h2>
       <div className="flex justify-center items-center">
         <ElectricBorder
           color="#80ff9f"
           speed={1}
           chaos={0.15}
           borderRadius={16}
         >
           <div className="bg-gray-800 rounded-lg p-8">
             <div className="flex items-center justify-center h-24">
               <img
                 src="https://upload.wikimedia.org/wikipedia/commons/2/24/LEGO_logo.svg"
                 alt="LEGO"
                 className="h-20 object-contain"
               />
             </div>
           </div>
         </ElectricBorder>
       </div>
     </Reveal>
   </section>

      {/* Contact Section */}
      <section ref={contactRef} className="py-20 px-4 sm:px-6 lg:px-8 bg-black border-t border-lime-500/10">
        <Reveal className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">Get In Touch</h2>
          <p className="text-gray-300 mb-8">
            We're always open to discussing new projects, creative ideas, or opportunities to be part of your vision.
          </p>
          <div className="flex justify-center gap-6 mb-8">
          {/* Contact Section - Add social links when set up*/}
          </div>
          <div className="flex justify-center">
            <a
              href="mailto:contact@zentrue.net"
              className="border border-lime-500 px-8 py-3 rounded-full font-semibold hover:bg-lime-500/10 transition-colors inline-block text-lime-400"
            >
              Contact Us
            </a>
          </div>
        </Reveal>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-lime-500/20 text-center text-gray-400 bg-black">
        <p>© 2026 ZenTrue™.</p>
      </footer>

      {/* CRT scanline + vignette overlay */}
      <div
        className="crt-overlay pointer-events-none fixed inset-0 z-50"
        aria-hidden="true"
        style={{
          backgroundImage: `
            repeating-linear-gradient(
              0deg,
              rgba(0, 0, 0, 0.18) 0px,
              rgba(0, 0, 0, 0.18) 1px,
              transparent 1px,
              transparent 3px
            ),
            radial-gradient(
              ellipse at center,
              transparent 55%,
              rgba(0, 0, 0, 0.55) 100%
            )
          `,
          mixBlendMode: 'multiply'
        }}
      />
    </div>
  );
}